import { useEffect, useState } from 'react';
import { fetchApi } from '../api/fetch';

/**
 * One-click cleanup affordance for tasks Danny created from the welcome-
 * email samples (or the in-app fallback) during onboarding. Visible only
 * when the user actually has welcome-tagged tasks less than 24h old —
 * the spec'd "first day" window.
 *
 * Banner self-hides:
 *   - After successful clear
 *   - After explicit dismiss
 *   - Once 24h has passed (count endpoint returns 0)
 */
export function SampleTaskBanner() {
  const [count, setCount] = useState<number | null>(null);
  const [clearing, setClearing] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('danny.sampleBannerDismissed') === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (dismissed) return;
    let alive = true;
    fetchApi<{ count: number }>('/onboarding/welcome-task-count')
      .then((res) => {
        if (alive) setCount(res?.count ?? 0);
      })
      .catch(() => {
        // Non-critical: if the endpoint isn't available (older deploy), hide.
        if (alive) setCount(0);
      });
    return () => {
      alive = false;
    };
  }, [dismissed]);

  if (dismissed || count === null || count === 0) return null;

  async function handleClear() {
    setClearing(true);
    try {
      await fetchApi('/onboarding/clear-welcome-tasks', { method: 'POST' });
      setCount(0);
    } catch (err) {
      console.warn('[SampleTaskBanner] clear failed', err);
    } finally {
      setClearing(false);
    }
  }

  function handleDismiss() {
    try {
      sessionStorage.setItem('danny.sampleBannerDismissed', '1');
    } catch {
      // ignore
    }
    setDismissed(true);
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-between gap-4 px-4 py-2.5 border-b border-violet-900/40 bg-violet-950/30 text-sm text-violet-100"
    >
      <p>
        <span className="text-violet-300 font-medium mr-1.5">
          {count === 1 ? '1 task' : `${count} tasks`}
        </span>
        from your welcome email. Clear them when you've made them your own.
      </p>
      <div className="flex items-center gap-3">
        <button
          onClick={handleClear}
          disabled={clearing}
          className="px-3 py-1 text-xs font-medium rounded-md bg-violet-700/60 hover:bg-violet-600 disabled:opacity-50 text-white transition-colors"
        >
          {clearing ? 'Clearing…' : 'Clear sample tasks'}
        </button>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss banner"
          className="text-violet-400 hover:text-violet-200 text-lg leading-none"
        >
          ×
        </button>
      </div>
    </div>
  );
}
